import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { TextInput, Platform } from 'react-native'
import { connect } from 'react-redux'
import { INPUT, inputTypes, bindingTypes } from '@adalo/constants'

import { evaluateBinding } from '../../utils/dependencies'

import { executeAction, actionContextTypes } from '../../utils/actions'

import {
  changeValue,
  setDefaultValue,
  resetValue,
  getValue,
} from '../../ducks/formInputs'

import BaseObject from '../BaseObject'
import ActionWrapper from '../ActionWrapper'

export class Input extends BaseObject {
  static contextTypes = {
    ...actionContextTypes,
  }

  handleChange = value => {
    let { changeValue } = this.props
    const inputId = this.getInputId()

    value = this.formatValue(value)

    return changeValue(inputId, value)
  }

  executeIndividualInput = async component => {
    // Look at this.props.object for id
    let {
      object: { id: inputId },
    } = this.props
    let buttonId = component.inputActions[inputId].buttonId
    let actionChainId = component.inputActions[inputId].actionId
    let actionChain = component.actions[buttonId][actionChainId].actions
    let wrappedAction = {
      actions: actionChain,
    }
    await executeAction(wrappedAction, { ...this.context })
    let { setDirty } = this.context
    setDirty()
  }

  formatValue = val => {
    let { object } = this.props
    let { inputType } = object.attributes

    if (object.type === INPUT) {
      if (inputType === inputTypes.NUMBER) {
        if (val.match(/[^\-\d\.]/)) {
          return val.replace(/[^\-\d\.]/g, '')
        }
      }

      if (inputType === inputTypes.LOWERCASE) {
        return val.toLowerCase()
      }
    }

    return val
  }

  setDefaultValue = value => {
    let { setDefaultValue } = this.props
    const inputId = this.getInputId()

    return setDefaultValue(inputId, value)
  }

  getInputId() {
    let { object, getBindingsList } = this.props
    const parentBindings = getBindingsList()

    return parentBindings.concat(object.id).join('.')
  }

  inputRef = input => {
    this.input = input
  }

  getDefaultValue = () => {
    let { object, bindingData, defaultValue } = this.props
    let { dataBinding } = object

    if (defaultValue) {
      return defaultValue
    } else if (dataBinding && dataBinding.type === bindingTypes.SET_TEXT) {
      return bindingData
    }
  }

  componentDidMount() {
    let defaultValue = this.getDefaultValue()

    this.setDefaultValue(defaultValue)
  }

  componentDidUpdate(oldProps) {
    this.setDefaultValue(this.getDefaultValue())
  }

  render() {
    let {
      topScreen,
      object,
      component,
      value,
      bindingData,
      defaultValue,
    } = this.props

    let { attributes, layout } = object

    let {
      autoFocus,
      placeholder,
      placeholderColor,
      fontSize,
      padding,
      height,
      multiline,
      inputType,
      maxLength,
    } = attributes

    placeholderColor = this.getColor(placeholderColor)

    let textStyles = this.textStyles()

    let styles = {
      ...textStyles,
      height,
      paddingLeft: padding,
      paddingRight: padding,
      paddingTop: 0,
      paddingBottom: 0,
      lineHeight: fontSize * 1.25,
    }

    if (multiline) {
      Object.assign(styles, {
        textAlignVertical: 'top',
        paddingTop: padding,
        paddingBottom: padding,
      })
    }

    let bgStyles = this.backgroundStyles()
    bgStyles.backgroundColor = bgStyles.backgroundColor || 'white'

    let inputStyle = {
      ...styles,
      ...this.shadowStyles(),
      ...this.borderStyles(),
      ...bgStyles,
    }

    if (Platform.OS === 'web') {
      inputStyle.outline = 'none'
    }

    let fallback = defaultValue

    if (
      !defaultValue &&
      object.dataBinding &&
      object.dataBinding.bindingType === bindingTypes.SET_TEXT
    ) {
      fallback = bindingData
    }

    if (!fallback) {
      fallback = ''
    }

    let autoCapitalize = inputType === inputTypes.DEFAULT ? 'sentences' : 'none'

    let keyboardType = 'default'
    let autoCorrect = true
    let secureTextEntry = false

    if (inputType === inputTypes.EMAIL) {
      keyboardType = 'email-address'
    } else if (inputType === inputTypes.NUMBER) {
      keyboardType = 'numeric'
    } else if (inputType === inputTypes.PASSWORD) {
      autoCorrect = false
      secureTextEntry = true
    }

    if (typeof value === 'number') {
      value = String(value)
    }

    return (
      <ActionWrapper
        object={object}
        component={component}
        bindingData={bindingData}
        style={layout}
      >
        <TextInput
          autoCapitalize={autoCapitalize}
          autoCorrect={autoCorrect}
          autoFocus={topScreen && autoFocus}
          keyboardType={keyboardType}
          style={inputStyle}
          multiline={multiline}
          ref={this.inputRef}
          returnKeyType={multiline ? 'default' : 'done'}
          onChangeText={this.handleChange}
          value={typeof value === 'string' ? value : ''}
          placeholder={placeholder}
          placeholderTextColor={placeholderColor}
          secureTextEntry={secureTextEntry}
          underlineColorAndroid="transparent"
          maxLength={maxLength}
        />
      </ActionWrapper>
    )
  }
}

export const connectInput = (inputClass, additionalMethods = {}, mapState) => {
  if (!mapState) {
    mapState = () => ({})
  }

  const mapStateToProps = (state, props) => {
    const { getBindingsList, object } = props
    const inputId = getBindingsList()
      .concat([object.id])
      .join('.')

    return {
      value: getValue(state, inputId),
      ...mapState(state, props),
    }
  }

  const ConnectedInput = connect(mapStateToProps, {
    ...additionalMethods,
    changeValue,
    setDefaultValue,
    resetValue,
  })(inputClass)

  class WrappedInput extends Component {
    static contextTypes = {
      getBindingsList: PropTypes.func,
    }

    render() {
      const { getBindingsList } = this.context

      return (
        <ConnectedInput {...this.props} getBindingsList={getBindingsList} />
      )
    }
  }

  return WrappedInput
}

const inputMapState = (state, props) => {
  let { defaultValue } = props.object.attributes

  return {
    defaultValue: evaluateBinding(state, defaultValue, props),
  }
}

export default connectInput(Input, null, inputMapState)
